import { Data } from "../models/data";
import { IStoreLocation, IStoreLocationZone } from "../models/data.interface";
import { Util } from "../utils/util";
import { HttpService } from "./http-service";
import { IService } from "./service.interface";
import { User } from '../models/user';
import { IUserAddress } from '../models/user.interface';
import { IAddressZone } from "../online-ordering/interfaces/online-ordering.interface";
import { AddressValidation } from "../types/enums";
import { OrderTime, DeliveryZoneResult } from "./location-service.interface";

export class LocationService implements IService {
    private readonly _http: HttpService;
    private readonly _data: Data;
    private readonly _apiLocation: string;

    constructor(http: HttpService, data: Data, apiLocation: string) {
        this._http = http;
        this._data = data;
        this._apiLocation = apiLocation;
    }

    public httpRequestAsync(method: string, url: string, payload: object | null = null, options?: { showProgress?: boolean, noCache?: boolean }): Promise<Response> {
        const storeKey = this._data.getProperty("StoreInfo")?.storeKey
            ?? this._data.getBrandProperty("StoreInfo").storeKey;

        const timeOffset = this._data.getProperty("TimeOffset")
            ?? this._data.getBrandProperty("TimeOffset");

        const businessStartMinute = this._data.getProperty("Settings")?.BSM
            ?? this._data.getBrandProperty("Settings").BSM;

        let businessDate = new Date().toISOString();
        try {
            businessDate = Util.GetBUSDate(Util.NowStore(timeOffset), timeOffset, businessStartMinute).toISOString();
        } catch { }

        return this._http.request(method, url, payload, storeKey, businessDate, { showProgress: options?.showProgress, noCache: options?.noCache });
    }

    public loadDeliveryZonesForLocations = async (storeIds: number[]): Promise<{ storeKey: string, zones: IStoreLocationZone[] }[]> => {
        const response = await this.httpRequestAsync("GET", `${this._apiLocation}stores-delivery-zones/?stores=${JSON.stringify(storeIds)}`);
        if (response.status == 200) {
            const respData = await this._http.getJson(response);
            if (respData && respData.result != "")
                return this._mapLocationDeliveryZonesData(JSON.parse(respData.result));
        }
        return [];
    }

    /**
     * Fetches the closest store(s) for the given brand and order types based on the user's location.
     * @param brandId - The ID of the brand.
     * @param orderTypeIds - An array of order type IDs to filter stores by.
     * @param latitude - The user's latitude.
     * @param longitude - The user's longitude.
     * @returns A list of closest store locations, or an empty array if the request fails or yields no results.
     */
    public async getClosestStoresAsync(brandId: string, orderTypeIds: number[], latitude: number, longitude: number): Promise<IStoreLocation[]> {
        let url = `${this._apiLocation}closest/${brandId}?latitude=${latitude}&longitude=${longitude}`;
        for (const orderTypeId of orderTypeIds) {
            url += `&orderTypeIds=${orderTypeId}`;
        }

        const response = await this.httpRequestAsync("GET", url);
        if (response.status === 200) {
            const respData = await this._http.getJson(response);
            if (respData && respData.result !== "")
                return this._data.mapLocationsData(JSON.parse(respData.result));
        }
        return [];
    }

    public async getClosestDeliveryStore(brandId: string, latitude: number, longitude: number): Promise<string> {
        const response = await this.httpRequestAsync(
            "GET",
            `${this._apiLocation}closest-delivery-store/${brandId}?latitude=${latitude}&longitude=${longitude}`
        );
        if (response.status === 200) {
            const respData = await this._http.getJson(response);
            if (respData && respData.result !== "") {
                return respData.result;
            }
        }
        return "";
    }

    public async getClosestDeliveryStoreKeyByUserAsync(brandId: string, user: User): Promise<string> {
        const primaryAddress = user.getProperty("Addresses")?.find((x: IUserAddress) => x.IPRM);
        if (primaryAddress) {
            const closestDeliveryStoreKey = await this.getClosestDeliveryStore(brandId, primaryAddress.LAT, primaryAddress.LON);
            return closestDeliveryStoreKey;
        }
        return "";
    }

    private readonly _mapLocationDeliveryZonesData = (data: any): { storeKey: string, zones: IStoreLocationZone[] }[] => {
        const mappedData = data.map((x: any) => ({
            storeKey: x.SKEY,
            zones: x.ZONs?.map((y: any) => ({
                storeId: y.SID,
                id: y.ZID,
                typeId: y.ZTID,
                charge: y.ZCHRG,
                minimumDeliveryAmount: y.MINDEL,
                isLimitedTime: y.ILT,
                limitedTimes: y.LIMTIME?.map((z: any) => ({
                    weekdayId: z.WID,
                    startMinute: z.SMIN,
                    endMinute: z.EMIN
                })) || [],
                polygons: y.PGON?.map((z: any) => ({
                    latitude: z.LAT,
                    longitude: z.LON
                })) || []
            })) || []
        })) || [];

        return mappedData;
    }

    // Resolves the effective order time from order state.
    // Accepts primitives so LocationService has no dependency on Order.
    public resolveOrderTime(
        futureDate: Date | null,
        futureMinute: number | null,
        timeOffset: number
    ): OrderTime {
        if (futureDate) {
            return {
                weekday: futureDate.getUTCDay(),
                minuteOfDay: futureMinute ?? 0
            };
        }

        const now = Util.NowStore(timeOffset);
        return {
            weekday: now.getDay(),
            minuteOfDay: now.getHours() * 60 + now.getMinutes()
        };
    }

    // Pure time-window check against a single zone's LIMTIME schedule.
    public isZoneDeliverableAt(zone: IAddressZone, time: OrderTime): boolean {
        const schedule = zone.LIMTIME.find(s => s.weekdayId === time.weekday);
        return !!schedule
            && schedule.startMinute < time.minuteOfDay
            && time.minuteOfDay < schedule.endMinute;
    }

    // Finds the first store whose zones cover the given coordinates,
    // applying all delivery rules (ZTID, ThirdParty, ILT).
    // Zones must already be loaded by the caller.
    public findDeliveryZoneForAddress(
        latitude: number,
        longitude: number,
        stores: IStoreLocation[],
        orderTime: OrderTime
    ): DeliveryZoneResult {
        for (const store of stores) {
            const zoneStatus = Util.checkZones(latitude, longitude, store.zones);

            if (!zoneStatus) continue;

            const isServicableZone = zoneStatus.ZTID === 1 ||
                (store.settings.ThirdPartyDelService && store.settings.ThirdPartyDeliveryMethod === 1);

            if (!isServicableZone) continue;

            if (zoneStatus.ILT && !this.isZoneDeliverableAt(zoneStatus, orderTime)) {
                return {
                    status: AddressValidation.OUT_OF_TIME,
                    storeKey: store.storeKey,
                    zoneId: zoneStatus.ZID
                };
            }

            return {
                status: AddressValidation.VALID,
                storeKey: store.storeKey,
                zoneId: zoneStatus.ZID
            };
        }

        return {
            status: AddressValidation.OUT_OF_DELIVERY_ZONE,
            storeKey: "",
            zoneId: 0
        };
    }
}